import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ApiCoreService, BEResponse } from '@qtek/core/api-core';
import { AuthUserStore } from '@qtek/libs/auth-user';
import { MetaCoreActions, MetaCoreFeature } from '@qtek/libs/meta-core';
import { format } from 'date-fns';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  interval,
  map,
  mergeMap,
  Observable,
  repeat,
  retry,
  shareReplay,
  Subscription,
  take,
  tap,
  timer,
} from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  BaseWebsocketMessage,
  ErrorMessageResponse,
  InitWebsocketMessageRequest,
  isInitWS,
  isInitWSResponse,
  isKillWS,
  isPingWSResponse,
  isWSErrorResponse,
  isWSUnauthorized,
  passWhenAlive,
  PingWebsocketMessageRequest,
  WEBSOCKET_BASE_URL,
} from '../models/web-socket.model';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private readonly webSocketBaseUrl: string = inject(WEBSOCKET_BASE_URL);
  private readonly store: Store = inject(Store);
  private readonly apiCoreService: ApiCoreService = inject(ApiCoreService);
  private readonly isAuthorized$ = toObservable(
    inject(AuthUserStore).isAuthorized
  );

  private wsConnectionId = 0;
  private pingCounter = 0;
  private pongCounter = 0;
  private retryCounter = 0;
  private repeatCounter = 0;
  private isHandlingUnauthorized = false;
  private readonly isAliveSubject = new BehaviorSubject(false);
  private readonly isAlive$ = this.isAliveSubject
    .asObservable()
    .pipe(distinctUntilChanged()); //.pipe(share()); //.pipe(shareReplay({ bufferSize: 0, refCount: true }));
  private readonly paramsSubject = new BehaviorSubject<{
    res?: {
      prs?: { prsId: string };
      cmp?: { _id: string };
      wsTkn?: string;
    };
  }>({});
  private readonly params$ = this.paramsSubject.asObservable();
  private readonly retryOnError = (error: Error, retryCount: number) => {
    console.log(
      '[qtek] WS Service - retry',
      `Retry number ${this.retryCounter} time ${format(new Date(), 'HH:mm:ss:SSS')}`
    );
    this.pingSubscription?.unsubscribe();
    this.pingCounter = 0;
    this.pongCounter = 0;
    this.isAliveSubject.next(false);

    return timer(Math.min(20, Math.pow(this.retryCounter++ + 1, 2)) * 1000);
  };

  private readonly repeatOnClose = (retryCount: number) => {
    console.log(
      '[qtek] WS Service - repeat',
      `Repeat number ${this.repeatCounter} time ${format(new Date(), 'HH:mm:ss:SSS')}`
    );
    this.pingSubscription?.unsubscribe();
    this.pingCounter = 0;
    this.pongCounter = 0;
    this.isAliveSubject.next(false);
    return timer(Math.min(20, Math.pow(this.repeatCounter++ + 1, 2)) * 1000);
  };
  private socketSubject: WebSocketSubject<BaseWebsocketMessage> | null = null;
  private currentSocketSubscription: Subscription | null = null;
  private pingSubscription: Subscription | null = null;
  private readonly wsSocketSubject =
    new BehaviorSubject<WebSocketSubject<BaseWebsocketMessage> | null>(null);
  private readonly wsSocket$ = this.wsSocketSubject.asObservable();

  public init$ = this.isAlive$.pipe(filter(isAlive => isAlive));
  private refreshWSToken$: Observable<BEResponse<{ wsTkn: string }>> = null;

  initWebsocketConnection(
    pingPongDelay$: Observable<number>,
    errorHandler: (message: ErrorMessageResponse) => any,
    options?: {
      prsdId?: string;
      cmpId?: string;
      wsTkn?: string;
    }
  ): void {
    const option: {
      res?: {
        prs?: { prsId: string };
        cmp?: { _id: string };
        wsTkn?: string;
      };
    } = {
      ...(!!options?.prsdId || !!options?.wsTkn || !!options?.cmpId
        ? {
            res: {
              ...(options.prsdId ? { prs: { prsId: options?.prsdId } } : {}),
              ...(options.cmpId ? { cmp: { _id: options?.cmpId } } : {}),
              ...(options.wsTkn ? { wsTkn: options?.wsTkn } : {}),
            },
          }
        : {}),
    };

    if (
      this.isAliveSubject.getValue() &&
      option.res?.wsTkn === this.paramsSubject.getValue().res?.wsTkn &&
      option.res?.cmp === this.paramsSubject.getValue().res?.cmp &&
      option.res?.prs === this.paramsSubject.getValue().res?.prs
    ) {
      return;
    }

    this.currentSocketSubscription?.unsubscribe();
    this.socketSubject?.complete();
    this.socketSubject?.unsubscribe();

    this.paramsSubject.next(option);

    this.isAliveSubject.next(false);

    let connectionId: number;

    this.socketSubject = webSocket<BaseWebsocketMessage>({
      url: `${this.webSocketBaseUrl}/e`,
      openObserver: {
        next: () => {
          connectionId = this.wsConnectionId++;
          console.log(
            '[qtek] WS Service - Connection established: ',
            connectionId
          );
          this.refreshWSToken$ = null;
          this.isAliveSubject.next(false);

          const initMessage: InitWebsocketMessageRequest = {
            op: 'init',
            v: 'v1.22.7',
            ...(option ? { ...option } : {}),
          };

          this.socketSubject?.next(initMessage);

          this.pingSubscription?.unsubscribe();
          this.pingSubscription = pingPongDelay$
            .pipe(
              filter(delay => delay > 0),
              take(1),
              mergeMap(time => interval(time * 1000)),
              tap(() => {
                if (this.pingCounter !== this.pongCounter) {
                  this.reconnect();
                  return;
                }
                const pingMessage: PingWebsocketMessageRequest = {
                  op: 'ping',
                  id: this.pingCounter,
                };
                this.pingCounter++;
                this.socketSubject.next(pingMessage);

                this.repeatCounter = 0;
                this.retryCounter = 0;
              })
            )
            .subscribe();
        },
      },

      closeObserver: {
        next: closeEvent => {
          console.log(
            '[qtek] WS Service - next closeObserver ',
            connectionId,
            closeEvent
          );
          this.isAliveSubject.next(false);
        },
        error: () => {
          console.log('[qtek] WS Service - error closeObserver ', connectionId);
          this.isAliveSubject.next(false);
        },
        complete: () => {
          console.log(
            '[qtek] WS Service - complete closeObserver ',
            connectionId
          );
          this.isAliveSubject.next(false);
        },
      },
    });

    this.currentSocketSubscription = this.socketSubject
      .pipe(
        map(message => this.handleWSMessages(message, errorHandler)),
        repeat({ delay: this.repeatOnClose }),
        retry({ delay: this.retryOnError })
      )
      .subscribe();

    this.wsSocketSubject.next(this.socketSubject);
  }

  private handleWSMessages(
    message: BaseWebsocketMessage,
    errorHandler: (message: ErrorMessageResponse) => void
  ): BaseWebsocketMessage {
    this.handleWSErrorMessage(message, errorHandler);
    this.handleInitWSMessage(message);
    this.handlePingWSMessage(message);
    this.handleKillWSMessage(message);

    return message;
  }

  private handleWSErrorMessage(
    message: BaseWebsocketMessage,
    errorHandler: (message: ErrorMessageResponse) => void
  ): void {
    if (!isWSErrorResponse(message)) {
      return;
    }

    if (isWSUnauthorized(message)) {
      this.handleUnauthorizedError();
      return;
    }

    errorHandler(message);
  }

  private handleInitWSMessage(message: BaseWebsocketMessage): void {
    if (!isInitWS(message) || !isInitWSResponse(message)) {
      return;
    }

    this.isAliveSubject.next(true);
    this.handleWSVersion(message.v);
    this.handleWSPingPongDelay(message.ppInt);
  }

  private handleWSVersion(version: string | undefined): void {
    if (!version || typeof version !== 'string') {
      return;
    }

    this.store.dispatch(MetaCoreActions.setWebsocketVersion({ version }));
  }

  private handleWSPingPongDelay(pingPongDelay: number | undefined): void {
    if (!pingPongDelay || typeof pingPongDelay !== 'number') {
      return;
    }

    this.store.dispatch(
      MetaCoreActions.setWebsocketPingPongDelay({ pingPongDelay })
    );
  }

  private handlePingWSMessage(message: BaseWebsocketMessage): void {
    if (!isPingWSResponse(message)) {
      return;
    }

    this.pongCounter++;
  }

  private handleKillWSMessage(message: BaseWebsocketMessage): void {
    if (!isKillWS(message)) {
      return;
    }

    this.refreshWSToken$ = this.apiCoreService
      .getWsSessionInitToken()
      .pipe(shareReplay(1), take(1));
    this.refreshWSToken$.subscribe();
    this.reconnect();
  }

  /**
   * Handles unauthorized errors by refreshing token
   */
  private handleUnauthorizedError(): void {
    // Prevent multiple simultaneous unauthorized error handlers
    if (this.isHandlingUnauthorized) {
      return;
    }

    this.isHandlingUnauthorized = true;
    console.log(
      '[qtek] WS Service - Handling unauthorized error - refreshing token'
    );

    if (this.refreshWSToken$ === null) {
      this.refreshWSToken$ = this.apiCoreService
        .getWsSessionInitToken()
        .pipe(shareReplay(1));
    }

    this.refreshWSToken$
      .pipe(
        tap((response: BEResponse<{ wsTkn: string }>) => {
          this.isHandlingUnauthorized = false; // Reset flag before reconnecting
          this.socketSubject.complete();
          this.initWebsocketConnection(
            this.store.select(MetaCoreFeature.selectWsPingPong),
            () => {
              // Error handler will be set by caller
            },
            { wsTkn: response.res?.wsTkn }
          );
        }),
        catchError(() => {
          this.isHandlingUnauthorized = false; // Reset flag on error
          return this.handleRefreshTokenError();
        }),
        take(1)
      )
      .subscribe();
  }

  /**
   * Handles refresh token error by waiting for authorization
   */
  private handleRefreshTokenError(): Observable<boolean> {
    return this.isAuthorized$.pipe(
      filter((isAuthorized): isAuthorized is boolean => Boolean(isAuthorized)),
      tap(() => {
        this.isHandlingUnauthorized = false; // Reset flag before reconnecting
        this.initWebsocketConnection(
          this.store.select(MetaCoreFeature.selectWsPingPong),
          () => {
            // Error handler will be set by caller
          },
          {}
        );
      })
    );
  }

  getWebSocket$(): Observable<BaseWebsocketMessage> {
    return this.wsSocket$.pipe(
      passWhenAlive(this.getWebSocketIsAlive$()),
      mergeMap(subject => subject)
    );
  }

  getWebSocketSubject(): Observable<WebSocketSubject<BaseWebsocketMessage>> {
    return this.wsSocket$;
  }

  getWebSocketIsAlive$(): Observable<boolean> {
    return this.isAlive$;
  }

  reconnect() {
    this.socketSubject.complete();
  }
}
